/**
 * /guides/:slug — how-to guides targeting high-intent search queries.
 *
 * Targets:
 *   "OpenAPI to MCP converter" → /guides/openapi-to-mcp
 *   "Claude Desktop MCP setup" → /guides/claude-desktop-mcp-setup
 *   "OAuth MCP setup"          → /guides/oauth-mcp-setup
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, ChevronDown, Copy,
  Clock, BookOpen, Zap,
} from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import { getGuideBySlug, GUIDES_DATA } from "../data/guidesData";
import NotFound from "./notfound";

// ── Copy button ───────────────────────────────────────────────────────────────

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="c" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }} className="flex items-center gap-1.5 text-emerald-400">
            <Check className="w-3 h-3" /> Copied
          </motion.span>
        ) : (
          <motion.span key="u" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }} className="flex items-center gap-1.5">
            <Copy className="w-3 h-3" /> Copy
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

// ── FAQ ───────────────────────────────────────────────────────────────────────

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="font-medium text-foreground pr-4">{q}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function GuidePage() {
  const { slug = "" } = useParams();
  const guide = getGuideBySlug(slug);

  if (!guide) return <NotFound />;

  const relatedGuides = guide.related
    .map((s) => GUIDES_DATA.find((g) => g.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SeoHead
        title={guide.pageTitle}
        description={guide.metaDescription}
        canonical={`/guides/${guide.slug}`}
      />
      <TopHeader />

      {/* ── Breadcrumb ── */}
      <div className="max-w-3xl mx-auto w-full px-4 pt-8 pb-0">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            Home
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">Guides</span>
          <span>/</span>
          <span className="text-foreground font-medium">{guide.headline}</span>
        </nav>
      </div>

      {/* ── Hero ── */}
      <section className="max-w-3xl mx-auto w-full px-4 pt-10 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
              <BookOpen className="w-3 h-3" />
              Guide
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border">
              <Clock className="w-3 h-3" />
              {guide.timeEstimate}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">
            {guide.headline}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {guide.subheadline}
          </p>
        </motion.div>
      </section>

      {/* ── Intro ── */}
      <section className="max-w-3xl mx-auto w-full px-4 pb-10">
        <p className="text-base text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-5">
          {guide.intro}
        </p>
      </section>

      {/* ── Steps ── */}
      <section className="max-w-3xl mx-auto w-full px-4 pb-16">
        <div className="space-y-8">
          {guide.steps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="flex gap-5"
            >
              {/* Step number */}
              <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{step.body}</p>

                {step.code && (
                  <div className="relative rounded-xl overflow-hidden border border-zinc-800">
                    <div className="flex items-center justify-between bg-zinc-900 px-4 py-2 border-b border-zinc-800">
                      <span className="text-xs font-mono text-zinc-400">{step.codeLabel ?? "Code"}</span>
                      <CopyButton text={step.code} />
                    </div>
                    <pre className="bg-zinc-950 text-zinc-100 text-xs leading-relaxed p-4 overflow-x-auto font-mono">
                      {step.code}
                    </pre>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Glossary ── */}
      {guide.glossary && guide.glossary.length > 0 && (
        <section className="max-w-3xl mx-auto w-full px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border border-border bg-card p-8"
          >
            <h2 className="text-lg font-bold mb-5">Key concepts</h2>
            <dl className="space-y-4">
              {guide.glossary.map((item) => (
                <div key={item.term}>
                  <dt className="text-sm font-semibold text-foreground mb-1">{item.term}</dt>
                  <dd className="text-sm text-muted-foreground leading-relaxed">{item.definition}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </section>
      )}

      {/* ── FAQ ── */}
      <section className="max-w-3xl mx-auto w-full px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
          <div className="space-y-3">
            {guide.faq.map((item) => (
              <FaqItem key={item.q} {...item} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Related guides ── */}
      {relatedGuides.length > 0 && (
        <section className="max-w-3xl mx-auto w-full px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-xl font-bold mb-5">Related guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedGuides.map((g) => g && (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  className="flex items-start gap-3 p-5 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-muted/30 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors mb-1 flex items-center gap-1.5">
                      {g.headline}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {g.timeEstimate}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="max-w-3xl mx-auto w-full px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-10 text-white text-center shadow-xl"
        >
          <h2 className="text-2xl font-bold mb-2">Ready to try it?</h2>
          <p className="text-blue-100 mb-7 text-base max-w-sm mx-auto">
            {guide.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={guide.primaryCtaHref}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors shadow text-sm"
            >
              <Zap className="w-4 h-4" />
              {guide.primaryCtaLabel}
            </Link>
            {guide.secondaryCtaLabel && guide.secondaryCtaHref && (
              <Link
                to={guide.secondaryCtaHref}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-white/15 border border-white/30 text-white font-semibold hover:bg-white/25 transition-colors text-sm"
              >
                {guide.secondaryCtaLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </motion.div>
      </section>

      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": guide.headline,
            "description": guide.metaDescription,
            "totalTime": `PT${guide.timeEstimate.replace(" minutes", "M")}`,
            "step": guide.steps.map((s, i) => ({
              "@type": "HowToStep",
              "position": i + 1,
              "name": s.title,
              "text": s.body,
            })),
          }),
        }}
      />

      <Footer />
    </div>
  );
}
